/* eslint-disable @typescript-eslint/no-explicit-any */
import DateFilterTemplate from 'apps/marsinet/src/app/components/date-template/date-template';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import {
  getIncidentPriorityColor,
  getIncidentStatusColor,
} from '../../commons/rps-incidence-helpers';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { incidenceBodyTemplate } from '../../commons/utils';
import {
  formatUtcTimestamp,
  getCalendarDateInTimeZone,
} from '../../../../../utils/timezone-utils';

type IncidentsListProps = {
  incidents?: any[];
  loading?: boolean;
  /** Bumped by the toolbar's "clear" button to reset the column filters. */
  clearFiltersKey?: number;
  /** Shared filters toolbar, rendered as the table header. */
  header?: React.ReactNode;
};

const ROWS_PER_PAGE = 10;

/**
 * Filter keys MUST match each Column's `field` (or `filterField`),
 * otherwise PrimeReact silently ignores them — or throws when the
 * filter menu is opened on a column that has no entry here.
 */
const createDefaultFilters = () => ({
  incident_code: { value: null, matchMode: FilterMatchMode.CONTAINS },
  description: { value: null, matchMode: FilterMatchMode.CONTAINS },
  device: { value: null, matchMode: FilterMatchMode.CONTAINS },
  status: { value: null, matchMode: FilterMatchMode.EQUALS },
  priority: { value: null, matchMode: FilterMatchMode.EQUALS },
  start_date: { value: null, matchMode: FilterMatchMode.DATE_IS },
});

const normalizeText = (value: any) =>
  String(value ?? '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');

export function IncidentsList({
  incidents = [],
  loading = false,
  clearFiltersKey = 0,
  header,
}: IncidentsListProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { selectedTimezone } = useContext(MarsinetContext);

  const [filters, setFilters] = useState<Record<string, any>>(
    createDefaultFilters
  );
  const [first, setFirst] = useState(0);
  const [pageRows, setPageRows] = useState(ROWS_PER_PAGE);

  // PrimeReact compara días del calendario; adapta cada fecha UTC al día de la zona seleccionada.
  const tableRows = useMemo(
    () =>
      incidents.map((incidence) => ({
        ...incidence,
        start_date: (() => {
          const value = incidence?.start_time ?? incidence?.start_date;
          const date =
            value instanceof Date ? value : value ? new Date(value) : null;
          return date && !Number.isNaN(date.getTime())
            ? getCalendarDateInTimeZone(date, selectedTimezone)
            : null;
        })(),
      })),
    [incidents, selectedTimezone]
  );

  const [sortField, setSortField] = useState<string | undefined>('start_date');
  const [sortOrder, setSortOrder] = useState<0 | 1 | -1 | null>(-1);

  const openIncidentDetails = (row: any) =>
    navigate(`/authenticated/incidents/${row.id}`, {
      state: {
        incidence: row,
        maintenanceOrders: row?.maintenance_orders || [],
      },
    });

  // The range and the scope are owned by the page toolbar; the column filters
  // are not, so they reset on the toolbar's signal instead.
  useEffect(() => {
    if (!clearFiltersKey) return;

    setFilters(createDefaultFilters());
    setFirst(0);
  }, [clearFiltersKey]);

  // Options for the status / priority dropdowns, taken from the loaded rows.
  const enumOptions = useMemo(() => {
    const collect = (field: 'status' | 'priority') =>
      Array.from(
        new Set(
          incidents
            .map((incidence) => String(incidence?.[field] ?? '').trim())
            .filter(Boolean)
        )
      ).map((value) => ({ label: value, value }));

    return { status: collect('status'), priority: collect('priority') };
  }, [incidents]);

  const enumFilterTemplate = (
    options: any,
    filterType: 'status' | 'priority'
  ) => {
    const itemTemplate = (option: { label: string; value: string }) => {
      const normalized = normalizeText(option.value);
      const color =
        filterType === 'status'
          ? getIncidentStatusColor(normalized)
          : getIncidentPriorityColor(normalized);

      return (
        <Tag
          style={{
            paddingLeft: '0.5rem',
            paddingRight: '0.5rem',
            backgroundColor: color,
            color: 'white',
          }}
          value={option.label}
        />
      );
    };

    return (
      <Dropdown
        value={options.value}
        options={enumOptions[filterType]}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={t(`pages.incidents.search_${filterType}`)}
        className="p-column-filter"
        showClear
        style={{ minWidth: '12rem' }}
        itemTemplate={itemTemplate}
      />
    );
  };

  const oneLineHeader = (label: React.ReactNode) => (
    <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>
      {label}
    </span>
  );

  const textBody = (value: any) => (
    <div
      className="pl-1 whitespace-nowrap overflow-hidden text-ellipsis"
      title={value || ''}
    >
      {value || <span className="text-400 italic">N/A</span>}
    </div>
  );

  const startDateBody = (row: any) => {
    const startDate: Date | null =
      row?.start_date instanceof Date
        ? row.start_date
        : row?.start_time
          ? new Date(row.start_time)
          : null;

    if (!startDate) {
      return <span className="pl-5 text-400 italic">N/A</span>;
    }

    const formattedDate = formatUtcTimestamp(
      startDate,
      selectedTimezone,
      i18n.language,
      { dateStyle: 'short' }
    );
    const time = formatUtcTimestamp(
      startDate,
      selectedTimezone,
      i18n.language,
      { hour: '2-digit', minute: '2-digit' }
    );

    return (
      <div
        className="pl-5 whitespace-nowrap text-sm"
        title={`${formattedDate} ${time}`}
      >
        <span className="block font-medium text-800">
          {formattedDate}
        </span>
        <span className="text-xs text-800">{time}</span>
      </div>
    );
  };

  return (
    <div>
      <DataTable
        value={tableRows}
        loading={loading}
        dataKey="id"
        paginator
        selectionMode="single"
        onSelectionChange={(e) => openIncidentDetails(e.value)}
        first={first}
        rows={pageRows}
        rowsPerPageOptions={[5, 10, 25, 50]}
        removableSort
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(e) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder as 0 | 1 | -1 | null);
          setFirst(0);
        }}
        onPage={(e) => {
          setFirst(e.first);
          setPageRows(e.rows);
        }}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        emptyMessage={t('emptyValuesTable')}
        header={header}
        filters={filters}
        filterDisplay="menu"
        onFilter={(e) => {
          setFilters(e.filters);
          setFirst(0);
        }}
        tableStyle={{ tableLayout: 'fixed' }}
      >
        <Column
          field="incident_code"
          header={oneLineHeader(t('pages.menu.ID_incident'))}
          filter
          filterPlaceholder={t('pages.incidents.search_title')}
          showFilterMatchModes={false}
          style={{ width: '11.5rem', minWidth: '11.5rem' }}
          body={(row) => textBody(row.incident_code)}
          sortable
        />

        <Column
          field="description"
          header={t('pages.incidents.description')}
          filter
          filterPlaceholder={t('pages.incidents.description')}
          showFilterMatchModes={false}
          style={{ width: '16rem' }}
          body={(row) => textBody(row.description)}
        />

        <Column
          field="device"
          header={t('pages.incidents.device_name')}
          filter
          filterPlaceholder={t('pages.incidents.search_device_name')}
          showFilterMatchModes={false}
          style={{ width: '10rem' }}
          body={(row) => textBody(row.device)}
          sortable
        />

        <Column
          field="status"
          header={t('pages.incidents.status')}
          filter
          showFilterMatchModes={false}
          filterElement={(options) => enumFilterTemplate(options, 'status')}
          style={{ width: '8rem' }}
          body={(row) =>
            incidenceBodyTemplate(
              row,
              'status',
              getIncidentPriorityColor,
              getIncidentStatusColor
            )
          }
          sortable
        />

        <Column
          field="priority"
          header={t('pages.incidents.priority')}
          filter
          showFilterMatchModes={false}
          filterElement={(options) => enumFilterTemplate(options, 'priority')}
          style={{ width: '10rem' }}
          body={(row) =>
            incidenceBodyTemplate(
              row,
              'priority',
              getIncidentPriorityColor,
              getIncidentStatusColor
            )
          }
          sortable
        />

        <Column
          field="start_date"
          dataType="date"
          header={oneLineHeader(t('pages.incidents.start_time'))}
          filter
          showFilterMatchModes={false}
          showFilterOperator={false}
          showAddButton={false}
          filterElement={(options) => <DateFilterTemplate options={options} />}
          style={{ width: '14rem', minWidth: '14rem' }}
          body={startDateBody}
          sortable
        />
      </DataTable>

    </div>
  );
}

export default IncidentsList;
